import type React from 'react';
import { Check, Store } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useI18nContext } from '@/i18n/i18n-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Skeleton } from '@/components/ui/skeleton';
import { useEnrolledTenants } from '@/hooks/use-enrolled-tenants';
import { useTenant } from '@/hooks/use-tenant';

export interface StoreSwitcherSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Mobile store switcher (SHELL-04 AC 2/3): search, the active store marked,
 * switching in the same tab. Query keys are per slug, so no cache reset.
 */
export function StoreSwitcherSheet({
  open,
  onOpenChange,
  returnFocusRef,
}: StoreSwitcherSheetProps) {
  const { LL } = useI18nContext();
  const navigate = useNavigate();
  const tenant = useTenant();
  const { data: tenants, isError, refetch } = useEnrolledTenants();

  const choose = (slug: string) => {
    onOpenChange(false);
    if (slug !== tenant.slug) navigate(`/${slug}/dashboard`);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent returnFocusRef={returnFocusRef}>
        <DrawerHeader className="pb-2 text-left">
          <DrawerTitle>{LL.tenantSwitcher.switchTenant()}</DrawerTitle>
          <DrawerDescription className="sr-only">
            {LL.shell.storeSwitcher.description()}
          </DrawerDescription>
        </DrawerHeader>

        <div className="min-h-0 px-4 pb-4">
          {isError ? (
            <div className="flex flex-col items-start gap-3 py-2">
              <p className="text-sm text-muted-foreground">
                {LL.shell.storeSwitcher.loadError()}
              </p>
              <Button
                type="button"
                variant="outline"
                className="min-h-touch"
                onClick={() => void refetch()}
              >
                {LL.shell.storeSwitcher.retry()}
              </Button>
            </div>
          ) : !tenants ? (
            <div className="flex flex-col gap-2 py-2">
              <Skeleton className="h-11 w-full" />
              <Skeleton className="h-11 w-full" />
            </div>
          ) : (
            <Command className="rounded-mobile-card border bg-background">
              <CommandInput
                placeholder={LL.shell.storeSwitcher.search()}
                className="text-base"
              />
              <CommandList className="max-h-[50dvh]">
                <CommandEmpty>{LL.shell.storeSwitcher.empty()}</CommandEmpty>
                <CommandGroup>
                  {tenants.map((t) => {
                    const active = t.slug === tenant.slug;
                    return (
                      <CommandItem
                        key={t.id}
                        value={t.slug}
                        keywords={[t.name]}
                        aria-current={active ? 'true' : undefined}
                        onSelect={() => choose(t.slug)}
                        className="min-h-touch gap-3"
                      >
                        <Avatar className="size-7 rounded-md">
                          <AvatarImage src={t.iconUrl} alt="" />
                          <AvatarFallback className="rounded-md">
                            <Store className="size-3.5" />
                          </AvatarFallback>
                        </Avatar>
                        <span className="flex-1 truncate">{t.name}</span>
                        {active && <Check className="size-4 text-primary" />}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
